import { useEffect, useMemo, useState } from "react";
import InfoIcon from "../svg/InfoIcon";
import { CustomDropdown } from "../ui/CustomDropdown";
import { NavigationButtons } from "../ui/NavigationButtons";
import { NumberStepper } from "../ui/NumberStepper";
import { SelectWithAddNew } from "../ui/SelectWithAddNew";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/indexRedux";
import { fetchPartners } from "../../store/partnerSlice";
import { PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  setHasPartner,
  setPartnerId,
  setController,
  setNominalPower,
  setNominalEnergy,
  setManufacturer,
  setSocMin,
  setSocMax,
  setRte,
  setCyclesPerYear,
} from "../../store/bessOnboarding";
import { sendBessContactSales } from "../../api/bessOnboarding";
import { formatNumber } from "../../utils/formatNumber";

// Yuso itself cannot be the commercial partner on a BESS contract.
const YUSO_PARTNER_ID = 1;

const DEFAULT_MANUFACTURERS = [
  "Octave",
  "Poweroad",
  "FIRN Energy",
  "SCU",
  "Huawei",
  "Bright Energy",
  "SAJ",
  "Tecloman",
  "Tesla",
  "BYD",
  "Sungrow",
  "CATL",
  "LG Energy Solution",
  "Samsung SDI",
  "Fluence",
  "Powin",
  "Wartsila",
  "SMA",
  "Saft",
  "Trina Storage",
  "Hithium",
];

const MANUFACTURER_REGEX = /^[A-Za-z0-9 .,&-]+$/;
const MANUFACTURER_MAX_LEN = 50;

function validateManufacturer(label: string): string | null {
  const trimmed = label.trim();
  if (trimmed.length === 0) return "Manufacturer cannot be empty.";
  if (trimmed.length > MANUFACTURER_MAX_LEN) return `Maximum ${MANUFACTURER_MAX_LEN} characters.`;
  if (!MANUFACTURER_REGEX.test(trimmed)) return "Only letters, digits, spaces and . , & - are allowed.";
  return null;
}

type StepNavProps = {
  onNext?: () => void;
  onPrevious?: () => void;
  onSubmit?: () => void;
};

function AssetSpec({ onNext, onPrevious }: StepNavProps) {
  const dispatch = useDispatch();

  const { partners, status, error } = useSelector((state: RootState) => state.partner);
  const accessToken = useSelector((state: RootState) => state.authv2.accessToken);
  const selectedCompany = useSelector((state: RootState) => state.selectedCompany.selectedUserCompany);

  const {
    hasPartner: reduxHasPartner,
    partnerId: reduxPartnerId,
    controller: reduxController,
    controllerId: reduxControllerId,
    nominalPower: reduxNominalPower,
    nominalEnergy: reduxNominalEnergy,
    manufacturer: reduxManufacturer,
    socMin: reduxSocMin,
    socMax: reduxSocMax,
    rte: reduxRte,
    cyclesPerYear: reduxCyclesPerYear,
    selectedOfftake,
    siteName,
  } = useSelector((state: RootState) => state.bessOnboarding);

  const [attemptedNext, setAttemptedNext] = useState(false);
  const [contactSalesStatus, setContactSalesStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [touched, setTouched] = useState({
    partnerChoice: false,
    partnerId: false,
    controller: false,
    nominalPower: false,
    nominalEnergy: false,
    manufacturer: false,
    socMin: false,
    socMax: false,
    rte: false,
    cyclesPerYear: false,
  });

  // Local list of manufacturer options; allows the user to add a new one for the session.
  // Pre-seed with the persisted value if the user previously added a custom one not in defaults.
  const [manufacturerOptions, setManufacturerOptions] = useState<string[]>(() => {
    if (reduxManufacturer && !DEFAULT_MANUFACTURERS.includes(reduxManufacturer)) {
      return [...DEFAULT_MANUFACTURERS, reduxManufacturer];
    }
    return DEFAULT_MANUFACTURERS;
  });

  // Restore touched state for fields that already have non-default values.
  useEffect(() => {
    const hasPartnerChoice = reduxHasPartner === "yes" || reduxHasPartner === "no";
    const hasPartnerValue = !!(reduxPartnerId && String(reduxPartnerId).trim() !== "");
    const hasControllerValue = !!(reduxController && String(reduxController).trim() !== "");
    const hasNominalPower = reduxNominalPower !== null && reduxNominalPower !== undefined;
    const hasNominalEnergy = reduxNominalEnergy !== null && reduxNominalEnergy !== undefined;
    const hasManufacturer = !!(reduxManufacturer && reduxManufacturer.trim() !== "");

    if (
      hasPartnerChoice ||
      hasPartnerValue ||
      hasControllerValue ||
      hasNominalPower ||
      hasNominalEnergy ||
      hasManufacturer
    ) {
      setTouched((prev) => ({
        ...prev,
        partnerChoice: hasPartnerChoice,
        partnerId: hasPartnerValue,
        controller: hasControllerValue,
        nominalPower: hasNominalPower,
        nominalEnergy: hasNominalEnergy,
        manufacturer: hasManufacturer,
      }));
    }
  }, [
    reduxHasPartner,
    reduxPartnerId,
    reduxController,
    reduxNominalPower,
    reduxNominalEnergy,
    reduxManufacturer,
  ]);

  useEffect(() => {
    if (status === "idle" && partners.length === 0 && accessToken) {
      dispatch(fetchPartners({ token: accessToken }) as unknown as PayloadAction);
    }
  }, [dispatch, status, partners.length, accessToken]);

  const flexPartners = partners.filter((p) => {
    if (p.id === YUSO_PARTNER_ID) return false;
    return p.partner_revenue_bess_default != null || p.partner_profit_share_bess_default != null;
  });

  // Existing flex services live on the offtake (it's the primary EAN selected in this flow and
  // the only endpoint that returns the enriched bess/eboiler/pvCurtailment blocks for this site).
  const existingPartnerId = useMemo(() => {
    if (selectedOfftake?.bess?.has_active_contract === 1 && selectedOfftake.bess.contract_partner_id != null)
      return selectedOfftake.bess.contract_partner_id;
    if (selectedOfftake?.eboiler?.has_active_contract === 1 && selectedOfftake.eboiler.contract_partner_id != null)
      return selectedOfftake.eboiler.contract_partner_id;
    if (
      selectedOfftake?.pvCurtailment?.has_active_contract === 1 &&
      selectedOfftake.pvCurtailment.contract_partner_id != null
    )
      return selectedOfftake.pvCurtailment.contract_partner_id;
    return null;
  }, [selectedOfftake]);

  const existingPartnerName = useMemo(() => {
    if (selectedOfftake?.bess?.has_active_contract === 1 && selectedOfftake.bess.contract_partner_id != null)
      return selectedOfftake.bess.contract_partner_name ?? null;
    if (selectedOfftake?.eboiler?.has_active_contract === 1 && selectedOfftake.eboiler.contract_partner_id != null)
      return selectedOfftake.eboiler.contract_partner_name ?? null;
    // PV Curtailment's nested object has no contract_partner_name — look it up in the partners list.
    if (
      selectedOfftake?.pvCurtailment?.has_active_contract === 1 &&
      selectedOfftake.pvCurtailment.contract_partner_id != null
    ) {
      const id = selectedOfftake.pvCurtailment.contract_partner_id;
      return partners.find((p) => p.id === id)?.name ?? null;
    }
    return null;
  }, [selectedOfftake, partners]);

  const affectedPartnerAssets = useMemo(() => {
    const assets: string[] = [];
    if (selectedOfftake?.bess?.has_active_contract === 1 && selectedOfftake.bess.contract_partner_id != null)
      assets.push("BESS");
    if (selectedOfftake?.eboiler?.has_active_contract === 1 && selectedOfftake.eboiler.contract_partner_id != null)
      assets.push("E-Boiler");
    if (
      selectedOfftake?.pvCurtailment?.has_active_contract === 1 &&
      selectedOfftake.pvCurtailment.contract_partner_id != null
    )
      assets.push("PV Curtailment");
    return assets;
  }, [selectedOfftake]);

  const partnerOptionsBase = [...flexPartners];
  if (
    existingPartnerId != null &&
    existingPartnerId !== YUSO_PARTNER_ID &&
    !flexPartners.some((p) => p.id === existingPartnerId)
  ) {
    const fallback = partners.find((p) => p.id === existingPartnerId);
    if (fallback) partnerOptionsBase.push(fallback);
  }

  const partnerOptions = partnerOptionsBase
    .map((p) => ({
      value: String(p.id),
      label: p.name,
      address: `${p.billingAddressStreet} ${p.billingAddressNumber}, ${p.billingAddressZip} ${p.billingAddressCity}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleNext = onNext ?? (() => {});
  const handlePrevious = onPrevious ?? (() => {});

  const [controllers, setControllers] = useState<{ id: number; name: string }[]>([]);
  const [controllersLoading, setControllersLoading] = useState(false);
  const [userChangedController, setUserChangedController] = useState(false);
  const [userChangedPartner, setUserChangedPartner] = useState(false);

  useEffect(() => {
    if (!accessToken) return;
    setControllersLoading(true);
    axios
      .get(`${import.meta.env.VITE_API_SERVICES || "http://localhost:8001"}/api/v1/sdp/controllers?product_id=1`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        setControllers(data);
      })
      .catch(() => {
        setControllers([]);
      })
      .finally(() => setControllersLoading(false));
  }, [accessToken]);

  const controllerOptions = controllers.map((c) => ({
    value: String(c.id),
    label: c.name,
  }));

  const affectedAssets = useMemo(() => {
    const assets: string[] = [];
    if (selectedOfftake?.bess?.has_active_contract === 1 && selectedOfftake.bess.controller_id != null)
      assets.push("BESS");
    if (selectedOfftake?.eboiler?.has_active_contract === 1 && selectedOfftake.eboiler.controller_id != null)
      assets.push("E-Boiler");
    if (
      selectedOfftake?.pvCurtailment?.has_active_contract === 1 &&
      selectedOfftake.pvCurtailment.controller_id != null
    )
      assets.push("PV Curtailment");
    return assets;
  }, [selectedOfftake]);

  const existingControllerId = useMemo(() => {
    if (selectedOfftake?.bess?.has_active_contract === 1 && selectedOfftake.bess.controller_id != null)
      return selectedOfftake.bess.controller_id;
    if (selectedOfftake?.eboiler?.has_active_contract === 1 && selectedOfftake.eboiler.controller_id != null)
      return selectedOfftake.eboiler.controller_id;
    if (
      selectedOfftake?.pvCurtailment?.has_active_contract === 1 &&
      selectedOfftake.pvCurtailment.controller_id != null
    )
      return selectedOfftake.pvCurtailment.controller_id;
    return null;
  }, [selectedOfftake]);

  const existingControllerName = useMemo(() => {
    if (selectedOfftake?.bess?.has_active_contract === 1 && selectedOfftake.bess.controller_id != null)
      return selectedOfftake.bess.controller_name ?? null;
    if (selectedOfftake?.eboiler?.has_active_contract === 1 && selectedOfftake.eboiler.controller_id != null)
      return selectedOfftake.eboiler.controller_name ?? null;
    if (
      selectedOfftake?.pvCurtailment?.has_active_contract === 1 &&
      selectedOfftake.pvCurtailment.controller_id != null
    )
      return selectedOfftake.pvCurtailment.controller_name ?? null;
    return null;
  }, [selectedOfftake]);

  const hasUncertifiedController = useMemo(() => {
    if (controllersLoading || controllers.length === 0) return false;
    if (existingControllerId == null) return false;
    return !controllers.some((c) => c.id === existingControllerId);
  }, [controllersLoading, controllers, existingControllerId]);

  useEffect(() => {
    if (userChangedController) return;
    if (existingControllerId == null || controllers.length === 0) return;
    if (reduxControllerId === existingControllerId) return;
    const match = controllers.find((c) => c.id === existingControllerId);
    if (match) {
      dispatch(setController({ name: match.name, id: match.id }));
    }
  }, [userChangedController, existingControllerId, reduxControllerId, controllers, dispatch]);

  useEffect(() => {
    if (userChangedPartner) return;
    if (existingPartnerId == null) return;
    if (reduxHasPartner !== "yes") {
      dispatch(setHasPartner("yes"));
    }
    if (existingPartnerId === YUSO_PARTNER_ID) return;
    if (String(reduxPartnerId) === String(existingPartnerId)) return;
    dispatch(setPartnerId(String(existingPartnerId)));
  }, [userChangedPartner, existingPartnerId, reduxPartnerId, reduxHasPartner, dispatch]);

  // ---------- Nominal power validation ----------
  const isNominalPowerMissing = reduxNominalPower == null || !Number.isFinite(Number(reduxNominalPower));
  const isNominalPowerBelowMin = !isNominalPowerMissing && (reduxNominalPower as number) < 50;
  const isNominalPowerHigh = !isNominalPowerMissing && (reduxNominalPower as number) >= 5000;

  // ---------- Nominal energy validation ----------
  const isNominalEnergyMissing = reduxNominalEnergy == null || !Number.isFinite(Number(reduxNominalEnergy));
  const isNominalEnergyBelowMin = !isNominalEnergyMissing && (reduxNominalEnergy as number) < 50;
  const isNominalEnergyHigh = !isNominalEnergyMissing && (reduxNominalEnergy as number) >= 5000;

  // ---------- Energy/power storage-duration plausibility (non-blocking warnings) ----------
  const hasBothNominals =
    !isNominalPowerMissing && !isNominalEnergyMissing && !isNominalPowerBelowMin && !isNominalEnergyBelowMin;
  const showEnergyBelowPowerWarning = hasBothNominals && (reduxNominalEnergy as number) < (reduxNominalPower as number);
  const showEnergyAboveMaxWarning = hasBothNominals && (reduxNominalEnergy as number) > 4 * (reduxNominalPower as number);

  // ---------- Manufacturer validation ----------
  const isManufacturerMissing = !reduxManufacturer || reduxManufacturer.trim() === "";

  // ---------- SoC validation ----------
  const isSocMinMissing = reduxSocMin == null;
  const isSocMaxMissing = reduxSocMax == null;
  const isSocMinOutOfRange =
    !isSocMinMissing && ((reduxSocMin as number) < 0 || (reduxSocMin as number) >= 100);
  const isSocMaxOutOfRange =
    !isSocMaxMissing && ((reduxSocMax as number) <= 0 || (reduxSocMax as number) > 100);
  const isSocOrderInvalid =
    !isSocMinMissing && !isSocMaxMissing && (reduxSocMin as number) >= (reduxSocMax as number);
  const showSocMinWarning = !isSocMinMissing && (reduxSocMin as number) > 20;
  const showSocMaxWarning = !isSocMaxMissing && (reduxSocMax as number) < 80;

  // ---------- RTE validation ----------
  const isRteMissing = reduxRte == null;
  const isRteOutOfRange =
    !isRteMissing && ((reduxRte as number) <= 0 || (reduxRte as number) > 100);
  const showRteWarning = !isRteMissing && ((reduxRte as number) < 85 || (reduxRte as number) > 95);

  // ---------- Cycles validation ----------
  const isCyclesMissing = reduxCyclesPerYear == null;
  const isCyclesOutOfRange =
    !isCyclesMissing && ((reduxCyclesPerYear as number) <= 0 || (reduxCyclesPerYear as number) >= 1000);
  const showCyclesWarning = !isCyclesMissing && (reduxCyclesPerYear as number) < 450;

  // ---------- Required-field checks (partner/controller) ----------
  const isHasPartnerMissing = reduxHasPartner !== "yes" && reduxHasPartner !== "no";
  const isPartnerMissing = reduxHasPartner === "yes" && (!reduxPartnerId || String(reduxPartnerId).trim() === "");
  const isControllerMissing = reduxControllerId == null;

  const blockNext =
    isHasPartnerMissing ||
    isPartnerMissing ||
    isControllerMissing ||
    isNominalPowerMissing ||
    isNominalPowerBelowMin ||
    isNominalEnergyMissing ||
    isNominalEnergyBelowMin ||
    isManufacturerMissing ||
    isSocMinMissing ||
    isSocMaxMissing ||
    isSocMinOutOfRange ||
    isSocMaxOutOfRange ||
    isSocOrderInvalid ||
    isRteMissing ||
    isRteOutOfRange ||
    isCyclesMissing ||
    isCyclesOutOfRange ||
    reduxHasPartner === "no";

  // ---------- Net energy capacity (derived) ----------
  const netEnergyCapacity = useMemo(() => {
    if (reduxNominalEnergy == null || reduxSocMin == null || reduxSocMax == null) return null;
    const value = ((reduxSocMax - reduxSocMin) / 100) * reduxNominalEnergy;
    if (!Number.isFinite(value) || value < 0) return null;
    return value;
  }, [reduxNominalEnergy, reduxSocMin, reduxSocMax]);

  function handleHasPartnerChange(val: "yes" | "no" | "") {
    dispatch(setHasPartner(val));
  }

  function handlePartnerSelect(val: string) {
    setUserChangedPartner(true);
    dispatch(setPartnerId(val));
    setTouched((s) => ({ ...s, partnerId: true }));
  }

  function handleControllerSelect(val: string) {
    setUserChangedController(true);
    const selected = controllers.find((c) => String(c.id) === val);
    dispatch(setController({ name: selected?.name || val, id: selected ? selected.id : null }));
    setTouched((s) => ({ ...s, controller: true }));
  }

  function handleManufacturerSelect(val: string) {
    dispatch(setManufacturer(val));
    setTouched((s) => ({ ...s, manufacturer: true }));
  }

  function handleManufacturerAdd(label: string) {
    const trimmed = label.trim();
    if (validateManufacturer(trimmed)) return;
    if (!manufacturerOptions.includes(trimmed)) {
      setManufacturerOptions((opts) => [...opts, trimmed]);
    }
    dispatch(setManufacturer(trimmed));
    setTouched((s) => ({ ...s, manufacturer: true }));
  }

  async function handleContactSales() {
    const companyId =
      selectedCompany?.company_id ||
      JSON.parse(localStorage.getItem("selectedUserCompany") || "{}")?.company_id ||
      null;
    if (!accessToken || !companyId) {
      setContactSalesStatus("error");
      return;
    }
    setContactSalesStatus("sending");
    try {
      await sendBessContactSales(accessToken, {
        company_id: Number(companyId),
        ...(siteName.trim() ? { site_name: siteName.trim() } : {}),
      });
      setContactSalesStatus("sent");
    } catch {
      setContactSalesStatus("error");
    }
  }

  function tryNext() {
    setAttemptedNext(true);
    setTouched({
      partnerChoice: true,
      partnerId: true,
      controller: true,
      nominalPower: true,
      nominalEnergy: true,
      manufacturer: true,
      socMin: true,
      socMax: true,
      rte: true,
      cyclesPerYear: true,
    });
    if (!blockNext) {
      handleNext();
    }
  }

  return (
    <div>
      <div className="mb-12">
        <h1 className="text-primary-600 dark:text-white text-3xl font-semibold mb-6">Asset specifications</h1>
        <p className="text-primary-500 dark:text-gray-300 font-normal text-base text-md">
          Please provide the technical specifications of your BESS installation. This helps us finalize your agreement.
        </p>
      </div>

      <div className="bg-white dark:bg-primary-900 p-12 rounded-lg w-full border-[2px] border-gray-100 dark:border-primary-800">
        {hasUncertifiedController ? (
          <>
            <div className="p-4 border-l-4 rounded-lg bg-red-50 border-red-500">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 pt-0.5">
                  <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-red-900 mb-2">Controller not supported for BESS</h3>
                  <p className="text-xs text-red-800">
                    The Yuso certified controller installed for your{" "}
                    <strong>{affectedAssets.join(" and ")}</strong> steering does not support BESS yet. In order to
                    proceed, the installed controller should pass our certification track for BESS.{" "}
                    <a href="mailto:bess@yuso.com" className="underline font-medium">
                      Please contact our BESS team
                    </a>{" "}
                    for more information.
                  </p>
                </div>
              </div>
            </div>
            <NavigationButtons currentStep={2} totalSteps={4} onPrevious={handlePrevious} onNext={undefined} />
          </>
        ) : (
          <>
            <form className="flex flex-col gap-8 text-sm" onSubmit={(e) => e.preventDefault()}>
              <h3 className="text-xl font-semibold text-primary-600 dark:text-white">Commercial setup</h3>

              {/* Partner selection */}
              <div className="flex flex-col gap-3">
                <label className="font-semibold text-primary-600 dark:text-white flex items-center gap-1">
                  Partner selection <span className="text-red-600">*</span>
                  <div
                    className="tooltip tooltip-primary"
                    data-tip="Yuso works with commercial partners that support the hardware and installation to get started with Yuso BESS. Contact a partner to take the next step."
                  >
                    <InfoIcon />
                  </div>
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Partner selection">
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer
                      ${
                        reduxHasPartner === "yes"
                          ? "bg-primary-50 dark:bg-primary-800 border-primary-600"
                          : "border-gray-200 dark:border-primary-700 hover:border-primary-200 dark:hover:border-primary-600"
                      }`}
                  >
                    <input
                      className="sr-only"
                      type="radio"
                      name="has-partner"
                      value="yes"
                      checked={reduxHasPartner === "yes"}
                      onChange={() => {
                        setUserChangedPartner(true);
                        handleHasPartnerChange("yes");
                        setTouched((s) => ({ ...s, partnerChoice: true }));
                      }}
                    />
                    <div
                      className={`flex items-center justify-center w-5 h-5 rounded-full border flex-shrink-0
                      ${reduxHasPartner === "yes" ? "bg-primary-600 border-primary-600" : "border-gray-300 dark:border-gray-600"}`}
                    >
                      {reduxHasPartner === "yes" && (
                        <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-primary-600 dark:text-white">
                        I have selected my Yuso commercial partner.
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Choose this if you&apos;ve already picked a partner to handle hardware and install.
                      </div>
                    </div>
                  </label>

                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer
                      ${
                        reduxHasPartner === "no"
                          ? "bg-primary-50 dark:bg-primary-800 border-primary-600"
                          : "border-gray-200 dark:border-primary-700 hover:border-primary-200 dark:hover:border-primary-600"
                      }`}
                  >
                    <input
                      className="sr-only"
                      type="radio"
                      name="has-partner"
                      value="no"
                      checked={reduxHasPartner === "no"}
                      onChange={() => {
                        setUserChangedPartner(true);
                        handleHasPartnerChange("no");
                        setTouched((s) => ({ ...s, partnerChoice: true }));
                      }}
                    />
                    <div
                      className={`flex items-center justify-center w-5 h-5 rounded-full border flex-shrink-0
                      ${reduxHasPartner === "no" ? "bg-primary-600 border-primary-600" : "border-gray-300 dark:border-gray-600"}`}
                    >
                      {reduxHasPartner === "no" && (
                        <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-primary-600 dark:text-white">
                        I want to get in touch with a Yuso commercial partner.
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Choose this if you&apos;d like us to connect you with a partner.
                      </div>
                    </div>
                  </label>
                </div>

                {(attemptedNext || touched.partnerChoice) && isHasPartnerMissing && (
                  <p className="text-xs text-red-600 mt-2">Please choose whether you have a partner or not.</p>
                )}

                {reduxHasPartner === "yes" && (
                  <div className="mt-3">
                    <label className="font-semibold text-primary-600 dark:text-white">
                      Partner name <span className="text-red-600">*</span>
                    </label>
                    <CustomDropdown
                      options={partnerOptions}
                      value={reduxPartnerId}
                      onChange={(v: string) => handlePartnerSelect(v)}
                      placeholder={status === "loading" ? "Loading partners..." : "Select partner"}
                      className="mt-1"
                      disabled={status === "loading" || flexPartners.length === 0}
                    />
                    {status === "failed" && <p className="text-xs text-red-600 mt-2">Failed to load partners: {error}</p>}
                    {flexPartners.length === 0 && status !== "loading" && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">No flex partners available.</p>
                    )}
                    {(attemptedNext || touched.partnerId) && isPartnerMissing && (
                      <p className="text-xs text-red-600 mt-2">Please select a partner.</p>
                    )}
                    {affectedPartnerAssets.length > 0 &&
                      (userChangedPartner && String(reduxPartnerId) !== String(existingPartnerId) ? (
                        <div className="bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-400 dark:border-amber-500 rounded-md p-3 text-sm text-amber-800 dark:text-amber-300 mt-2">
                          <strong>Warning:</strong> Changing this partner will also update the partner for your active{" "}
                          <strong>{affectedPartnerAssets.join(" and ")}</strong> contract
                          {affectedPartnerAssets.length > 1 ? "s" : ""}
                          {existingPartnerName ? (
                            <> (currently <strong>{existingPartnerName}</strong>)</>
                          ) : (
                            ""
                          )}
                          .
                        </div>
                      ) : (
                        <div className="bg-primary-50 dark:bg-primary-800 border-l-2 border-primary-200 dark:border-primary-600 rounded-md p-3 text-sm text-primary-600 dark:text-gray-300 mt-2">
                          <strong>This partner is shared across all assets on this site.</strong> Changing it will also
                          update the partner for your active <strong>{affectedPartnerAssets.join(" and ")}</strong>{" "}
                          contract{affectedPartnerAssets.length > 1 ? "s" : ""}
                          {existingPartnerName ? (
                            <> — currently set to <strong>{existingPartnerName}</strong></>
                          ) : (
                            ""
                          )}
                          .
                        </div>
                      ))}
                  </div>
                )}

                {reduxHasPartner === "no" && (
                  <div className="mt-3 bg-orange-50 border-2 border-orange-400 rounded p-4">
                    <p className="text-orange-800 font-medium mb-2">
                      I want to get in touch with a Yuso commercial partner.
                    </p>
                    <p className="text-orange-700 text-sm mb-2">
                      Choose this if you&apos;d like us to connect you with a partner.
                    </p>
                    <p className="text-orange-700 text-sm mb-2">
                      Visit our{" "}
                      <a
                        href="https://yuso.com/be/nl/partners"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-600 underline font-medium"
                      >
                        partner page
                      </a>{" "}
                      to view our commercial partners.
                    </p>
                    {contactSalesStatus === "sent" ? (
                      <p className="text-green-700 text-sm font-medium flex items-center gap-1.5">
                        <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Request sent. Our sales team will contact you shortly.
                      </p>
                    ) : (
                      <>
                        <p className="text-orange-700 text-sm mb-3">
                          Need help choosing the right partner for your needs? Contact our sales team, and
                          we&apos;ll connect you with a trusted partner.
                        </p>
                        <button
                          type="button"
                          onClick={handleContactSales}
                          disabled={contactSalesStatus === "sending"}
                          className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {contactSalesStatus === "sending" ? "Sending..." : "Contact our sales team"}
                        </button>
                        {contactSalesStatus === "error" && (
                          <p className="text-red-600 text-sm mt-2">
                            Something went wrong — please email{" "}
                            <a href="mailto:sales@yuso.com" className="underline font-medium">
                              sales@yuso.com
                            </a>{" "}
                            directly.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Controller dropdown */}
              <div className="flex flex-col gap-3">
                <label className="font-semibold text-primary-600 dark:text-white flex items-center gap-1">
                  Yuso certified controller <span className="text-red-600">*</span>
                  <div
                    className="tooltip tooltip-primary"
                    data-tip="The Yuso Certified Controller connects your BESS installation to Yuso's platform for smart steering. Partners typically provide one or more certified controllers."
                  >
                    <InfoIcon />
                  </div>
                </label>
                <CustomDropdown
                  options={controllerOptions}
                  value={reduxControllerId != null ? String(reduxControllerId) : ""}
                  onChange={(v: string) => handleControllerSelect(v)}
                  placeholder={controllersLoading ? "Loading controllers..." : "Select controller"}
                  disabled={controllersLoading || controllerOptions.length === 0}
                />
                {(attemptedNext || touched.controller) && isControllerMissing && (
                  <p className="text-xs text-red-600 mt-2">Please select a controller.</p>
                )}
                {affectedAssets.length > 0 ? (
                  userChangedController && reduxControllerId !== existingControllerId ? (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-400 dark:border-amber-500 rounded-md p-3 text-sm text-amber-800 dark:text-amber-300">
                      <strong>Warning:</strong> Changing this controller will also update the controller for your active{" "}
                      <strong>{affectedAssets.join(" and ")}</strong> contract{affectedAssets.length > 1 ? "s" : ""}
                      {existingControllerName ? (
                        <> (currently <strong>{existingControllerName}</strong>)</>
                      ) : (
                        ""
                      )}
                      .
                    </div>
                  ) : (
                    <div className="bg-primary-50 dark:bg-primary-800 border-l-2 border-primary-200 dark:border-primary-600 rounded-md p-3 text-sm text-primary-600 dark:text-gray-300">
                      <strong>This controller is shared across all assets on this site.</strong> Changing it will also
                      update the controller for your active <strong>{affectedAssets.join(" and ")}</strong> contract
                      {affectedAssets.length > 1 ? "s" : ""}
                      {existingControllerName ? (
                        <> — currently set to <strong>{existingControllerName}</strong></>
                      ) : (
                        ""
                      )}
                      .
                    </div>
                  )
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Select a certified controller used on-site.</p>
                )}
              </div>

              <h3 className="mt-4 text-xl font-semibold text-primary-600 dark:text-white">Battery specifications</h3>

              {/* Nominal power + Nominal energy capacity */}
              <div className="flex flex-col gap-2">
                <label className="font-semibold text-primary-600 dark:text-white">
                  Nominal power <span className="text-red-600">*</span>
                </label>
                <NumberStepper
                  value={reduxNominalPower}
                  onChange={(v) => {
                    dispatch(setNominalPower(v));
                    setTouched((s) => ({ ...s, nominalPower: true }));
                  }}
                  onBlur={() => setTouched((s) => ({ ...s, nominalPower: true }))}
                  step={50}
                  min={0}
                  decimals={2}
                  unit="kW"
                  placeholder="e.g. 250"
                  hasError={
                    (attemptedNext || touched.nominalPower) && (isNominalPowerMissing || isNominalPowerBelowMin)
                  }
                  ariaLabel="Nominal power in kW"
                />
                {(attemptedNext || touched.nominalPower) && isNominalPowerMissing && (
                  <p className="text-xs text-red-600">Please enter the nominal power.</p>
                )}
                {(attemptedNext || touched.nominalPower) && !isNominalPowerMissing && isNominalPowerBelowMin && (
                  <p className="text-xs text-red-600">Nominal power must be at least 50 kW.</p>
                )}
                {isNominalPowerHigh && (
                  <p className="text-xs text-amber-600">
                    For assets with capacity ≥ 5 MW, Yuso can provide a custom offer. Contact our{" "}
                    <a href="mailto:bess@yuso.com" className="underline hover:text-amber-700">
                      BESS team
                    </a>{" "}
                    to discuss your needs.
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label className="font-semibold text-primary-600 dark:text-white">
                  Nominal energy capacity <span className="text-red-600">*</span>
                </label>
                <NumberStepper
                  value={reduxNominalEnergy}
                  onChange={(v) => {
                    dispatch(setNominalEnergy(v));
                    setTouched((s) => ({ ...s, nominalEnergy: true }));
                  }}
                  onBlur={() => setTouched((s) => ({ ...s, nominalEnergy: true }))}
                  step={50}
                  min={0}
                  decimals={2}
                  unit="kWh"
                  placeholder="e.g. 500"
                  hasError={
                    (attemptedNext || touched.nominalEnergy) && (isNominalEnergyMissing || isNominalEnergyBelowMin)
                  }
                  ariaLabel="Nominal energy capacity in kWh"
                />
                {(attemptedNext || touched.nominalEnergy) && isNominalEnergyMissing && (
                  <p className="text-xs text-red-600">Please enter the nominal energy capacity.</p>
                )}
                {(attemptedNext || touched.nominalEnergy) && !isNominalEnergyMissing && isNominalEnergyBelowMin && (
                  <p className="text-xs text-red-600">Nominal energy capacity must be at least 50 kWh.</p>
                )}
                {showEnergyBelowPowerWarning && (
                  <p className="text-xs text-amber-600">
                    A typical BESS has a storage duration of at least one hour, equivalent to at least 1 kWh of capacity
                    per kW of nominal power.
                  </p>
                )}
                {showEnergyAboveMaxWarning && (
                  <p className="text-xs text-amber-600">
                    A typical BESS has a storage duration of no more than four hours, equivalent to at most 4 kWh of
                    capacity per kW of nominal power.
                  </p>
                )}
                {isNominalEnergyHigh && (
                  <p className="text-xs text-amber-600">
                    For assets with capacity ≥ 5 MWh, Yuso can provide a custom offer. Contact our{" "}
                    <a href="mailto:bess@yuso.com" className="underline hover:text-amber-700">
                      BESS team
                    </a>{" "}
                    to discuss your needs.
                  </p>
                )}
              </div>

              {/* Manufacturer */}
              <div className="flex flex-col gap-2">
                <label className="font-semibold text-primary-600 dark:text-white">
                  BESS manufacturer <span className="text-red-600">*</span>
                </label>
                <SelectWithAddNew
                  options={manufacturerOptions.map((m) => ({ value: m, label: m }))}
                  value={reduxManufacturer}
                  onChange={handleManufacturerSelect}
                  onAdd={handleManufacturerAdd}
                  validateNew={validateManufacturer}
                  placeholder="Select your system's manufacturer."
                />
                {(attemptedNext || touched.manufacturer) && isManufacturerMissing && (
                  <p className="text-xs text-red-600">Please select or add a manufacturer.</p>
                )}
              </div>

              <h3 className="mt-4 text-xl font-semibold text-primary-600 dark:text-white">Operating parameters</h3>

              {/* State of charge range */}
              <div className="flex flex-col gap-3">
                <label className="font-semibold text-primary-600 dark:text-white">
                  State of charge range <span className="text-red-600">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs text-gray-500 dark:text-gray-400">Minimum state of charge (SoC)</label>
                    <NumberStepper
                      value={reduxSocMin}
                      onChange={(v) => {
                        dispatch(setSocMin(v));
                        setTouched((s) => ({ ...s, socMin: true }));
                      }}
                      onBlur={() => setTouched((s) => ({ ...s, socMin: true }))}
                      step={1}
                      min={0}
                      max={99}
                      decimals={0}
                      unit="%"
                      placeholder="0"
                      hasError={
                        (attemptedNext || touched.socMin) && (isSocMinMissing || isSocMinOutOfRange || isSocOrderInvalid)
                      }
                      ariaLabel="Minimum state of charge"
                    />
                    {(attemptedNext || touched.socMin) && isSocMinMissing && (
                      <p className="text-xs text-red-600">Please enter a minimum SoC.</p>
                    )}
                    {(attemptedNext || touched.socMin) && !isSocMinMissing && isSocMinOutOfRange && (
                      <p className="text-xs text-red-600">Minimum SoC must be between 0% and 99%.</p>
                    )}
                    {(attemptedNext || touched.socMin || touched.socMax) &&
                      !isSocMinMissing &&
                      !isSocMaxMissing &&
                      isSocOrderInvalid && (
                        <p className="text-xs text-red-600">Minimum SoC must be less than maximum SoC.</p>
                      )}
                    {showSocMinWarning && (
                      <p className="text-xs text-amber-600">
                        A typical lithium ion BESS minimum SoC is below 20%. For other technologies, a different minimum
                        SoC is possible.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs text-gray-500 dark:text-gray-400">Maximum state of charge (SoC)</label>
                    <NumberStepper
                      value={reduxSocMax}
                      onChange={(v) => {
                        dispatch(setSocMax(v));
                        setTouched((s) => ({ ...s, socMax: true }));
                      }}
                      onBlur={() => setTouched((s) => ({ ...s, socMax: true }))}
                      step={1}
                      min={1}
                      max={100}
                      decimals={0}
                      unit="%"
                      placeholder="100"
                      hasError={(attemptedNext || touched.socMax) && (isSocMaxMissing || isSocMaxOutOfRange)}
                      ariaLabel="Maximum state of charge"
                    />
                    {(attemptedNext || touched.socMax) && isSocMaxMissing && (
                      <p className="text-xs text-red-600">Please enter a maximum SoC.</p>
                    )}
                    {(attemptedNext || touched.socMax) && !isSocMaxMissing && isSocMaxOutOfRange && (
                      <p className="text-xs text-red-600">Maximum SoC must be between 1% and 100%.</p>
                    )}
                    {showSocMaxWarning && (
                      <p className="text-xs text-amber-600">
                        A typical lithium ion BESS maximum SoC is above 80%. For other technologies, a different maximum
                        SoC is possible.
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex flex-col gap-1">
                  <span>
                    State of charge range:{" "}
                    <strong className="text-primary-600 dark:text-white">
                      {isSocMinMissing ? "—" : `${reduxSocMin}%`} –{" "}
                      {isSocMaxMissing ? "—" : `${reduxSocMax}%`}
                    </strong>
                  </span>
                  <span>
                    Net energy capacity:{" "}
                    <strong className="text-primary-600 dark:text-white">
                      {netEnergyCapacity == null ? "—" : `${formatNumber(netEnergyCapacity, "energy")} kWh`}
                    </strong>
                  </span>
                </div>
              </div>

              {/* Round-trip efficiency + Cycles per year */}
              <div className="flex flex-col gap-2">
                <label className="font-semibold text-primary-600 dark:text-white">
                  Round-trip efficiency (RTE) <span className="text-red-600">*</span>
                </label>
                <NumberStepper
                  value={reduxRte}
                  onChange={(v) => {
                    dispatch(setRte(v));
                    setTouched((s) => ({ ...s, rte: true }));
                  }}
                  onBlur={() => setTouched((s) => ({ ...s, rte: true }))}
                  step={1}
                  min={1}
                  max={100}
                  decimals={0}
                  unit="%"
                  placeholder="88"
                  hasError={(attemptedNext || touched.rte) && (isRteMissing || isRteOutOfRange)}
                  ariaLabel="Round-trip efficiency"
                />
                {(attemptedNext || touched.rte) && isRteMissing && (
                  <p className="text-xs text-red-600">Please enter the round-trip efficiency.</p>
                )}
                {(attemptedNext || touched.rte) && !isRteMissing && isRteOutOfRange && (
                  <p className="text-xs text-red-600">RTE must be between 1% and 100%.</p>
                )}
                {showRteWarning && (
                  <p className="text-xs text-amber-600">
                    A typical lithium ion BESS round-trip efficiency (RTE) is between 85% and 95%. For other technologies
                    (e.g. flow batteries), a different RTE is possible.
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label className="font-semibold text-primary-600 dark:text-white flex items-center gap-1">
                  Maximal annual throughput <span className="text-red-600">*</span>
                  <div
                    className="tooltip tooltip-primary"
                    data-tip="Number of charge-discharge cycles the BESS is configured to complete annually, with respect to the manufacturer's warranty conditions. A cycle is defined as the energy discharged relative to the nameplate energy capacity. (One cycle for a 2 MWh BESS means 2 MWh energy discharged.)"
                  >
                    <InfoIcon />
                  </div>
                </label>
                <NumberStepper
                  value={reduxCyclesPerYear}
                  onChange={(v) => {
                    dispatch(setCyclesPerYear(v));
                    setTouched((s) => ({ ...s, cyclesPerYear: true }));
                  }}
                  onBlur={() => setTouched((s) => ({ ...s, cyclesPerYear: true }))}
                  step={50}
                  min={1}
                  max={999}
                  decimals={0}
                  unit="cycles"
                  placeholder="600"
                  hasError={
                    (attemptedNext || touched.cyclesPerYear) && (isCyclesMissing || isCyclesOutOfRange)
                  }
                  ariaLabel="Maximal cycles per year"
                />
                {(attemptedNext || touched.cyclesPerYear) && isCyclesMissing && (
                  <p className="text-xs text-red-600">Please enter the maximal throughput in cycles per year.</p>
                )}
                {(attemptedNext || touched.cyclesPerYear) && !isCyclesMissing && isCyclesOutOfRange && (
                  <p className="text-xs text-red-600">Cycles per year must be between 1 and 999.</p>
                )}
                {showCyclesWarning && (
                  <p className="text-xs text-amber-600">
                    A typical lithium ion BESS guarantee conditions mention ≥ 450 cycles/year. Lower cycle numbers
                    affect revenue potential. Please refer to the manufacturer&apos;s terms for details on cycle limits.
                  </p>
                )}
              </div>
            </form>

            <NavigationButtons
              currentStep={2}
              totalSteps={4}
              onPrevious={handlePrevious}
              onNext={reduxHasPartner === "no" ? undefined : tryNext}
              nextDisabled={blockNext}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default AssetSpec;
